import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemText,
  ListProps,
  ListSubheader
} from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import type CatalogProduct from '../types/CatalogProduct'
import { useTranslation } from 'react-i18next'
import { formatApperentPower, formatNominalCurrent, formatNominalVoltage } from '../utils/string'
import {
  isAuroraCatalogProduct,
  isElectricalCabinetOrSpineModuleCatalogProduct,
  isGeneratorCatalogProduct,
  isHeaterCatalogProduct,
  isSpineLoadCatalogProduct,
  isSpineMultiMeasurementCatalogProduct,
  isValveCatalogProduct
} from '../utils/catalogProduct'
import type {
  SpineLoadCatalogProduct,
  SpineMultimeasurementCatalogProduct,
  ValveCatalogProduct
} from '../types/CatalogProduct'
import { useCallback, useEffect, useState } from 'react'
import uploadService from '../services/uploadService'
import ImageWithPreview from './ImageWithPreview'
import { CatalogProductLink, useLinkedCatalogProduct } from '../hooks/catalogProduct'

export interface CatalogProductDetailsListProps extends Omit<ListProps, 'children'> {
  catalogProduct: CatalogProduct
  showName?: boolean
  showManufacturer?: boolean
  showManufacturerProductCode?: boolean
  showCustomData?: boolean
}

export default function CatalogProductDetailsList({
  catalogProduct,
  showName = true,
  showManufacturer = true,
  showManufacturerProductCode = true,
  showCustomData = true,
  ...props
}: CatalogProductDetailsListProps) {
  const { t } = useTranslation('catalogProduct')

  const formatOutputs = (product: SpineMultimeasurementCatalogProduct) => {
    // Group by nominal current and count.
    const groups: { [key: string]: number } = {}
    for (const output of Object.values(product.customData.outputs)) {
      const key = `${output.nominalCurrent} A`
      groups[key] = (groups[key] || 0) + 1
    }

    // Format the groups into a string.
    return Object.entries(groups)
      .map(([key, count]) => `${count} x ${key}`)
      .join(' + ')
  }

  return (
    <List {...props}>
      <CatalogProductPhoto key="photo" catalogProduct={catalogProduct} />

      {showName && catalogProduct?.name && (
        <CatalogProductNameListItem key="name" catalogProduct={catalogProduct} />
      )}

      {showManufacturer && catalogProduct?.manufacturer && (
        <CatalogProductManufacturerListItem key="manufacturer" catalogProduct={catalogProduct} />
      )}

      {showManufacturerProductCode && catalogProduct?.manufacturerProductCode && (
        <CatalogProductManufacturerProductCodeListItem
          key="manufacturerProductCode"
          catalogProduct={catalogProduct}
        />
      )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.apparentPower != null && (
          <ListItemButton key="customData.apparentPower">
            <ListItemText
              primary={
                catalogProduct?.customData?.apparentPower != null
                  ? formatApperentPower(catalogProduct.customData.apparentPower)
                  : '-'
              }
              secondary={t('generator:apparentPower')}
            />
          </ListItemButton>
        )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.nominalVoltage != null && (
          <ListItemButton key="customData.nominalVoltage">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalVoltage != null
                  ? formatNominalVoltage(catalogProduct.customData.nominalVoltage)
                  : '-'
              }
              secondary={t('generator:nominalVoltage')}
            />
          </ListItemButton>
        )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.nominalCurrent != null && (
          <ListItemButton key="customData.nominalCurrent">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalCurrent != null
                  ? formatNominalCurrent(catalogProduct.customData.nominalCurrent)
                  : '-'
              }
              secondary={t('generator:nominalCurrent')}
            />
          </ListItemButton>
        )}

      {isGeneratorCatalogProduct(catalogProduct) &&
        showCustomData &&
        catalogProduct?.customData?.powerSource != null && (
          <ListItemButton key="customData.powerSource">
            <ListItemText
              primary={
                catalogProduct?.customData?.powerSource != null
                  ? t(`generator:${catalogProduct.customData.powerSource}`)
                  : '-'
              }
              secondary={t('generator:powerSource')}
            />
          </ListItemButton>
        )}

      {isElectricalCabinetOrSpineModuleCatalogProduct(catalogProduct) &&
        catalogProduct?.customData?.fuse != null && (
          <ListItemButton key="customData.fuse">
            <ListItemText
              primary={
                catalogProduct?.customData?.fuse != null
                  ? `${catalogProduct.customData.fuse} A`
                  : '-'
              }
              secondary={t('nominalCurrent')}
            />
          </ListItemButton>
        )}

      {isElectricalCabinetOrSpineModuleCatalogProduct(catalogProduct) &&
        catalogProduct?.customData?.inputCables != null && (
          <ListItemButton key="customData.inputCables">
            <ListItemText
              primary={
                catalogProduct?.customData?.inputCables != null
                  ? catalogProduct.customData.inputCables
                  : '-'
              }
              secondary={t('inputCables')}
            />
          </ListItemButton>
        )}

      {isElectricalCabinetOrSpineModuleCatalogProduct(catalogProduct) &&
        catalogProduct?.customData?.isMainDistributionAssembly != null && (
          <ListItemButton key="customData.isMainDistributionAssembly">
            <ListItemText
              primary={
                catalogProduct?.customData?.isMainDistributionAssembly != null
                  ? catalogProduct.customData.isMainDistributionAssembly
                    ? t('actions:yes')
                    : t('actions:nope')
                  : '-'
              }
              secondary={t('mainDistributionAssembly')}
            />
          </ListItemButton>
        )}

      {isHeaterCatalogProduct(catalogProduct) && catalogProduct.customData.heatingPower != null && (
        <ListItemButton key="customData.heatingPower">
          <ListItemText
            primary={
              catalogProduct?.customData?.heatingPower != null
                ? `${catalogProduct.customData.heatingPower} kW`
                : '-'
            }
            secondary={t('heater:heatingPower')}
          />
        </ListItemButton>
      )}

      {isAuroraCatalogProduct(catalogProduct) && catalogProduct.customData.heatingPower != null && (
        <ListItemButton key="customData.heatingPower">
          <ListItemText
            primary={
              catalogProduct?.customData?.heatingPower != null
                ? `${catalogProduct.customData.heatingPower} kW`
                : '-'
            }
            secondary={t('aurora:heatingPower')}
          />
        </ListItemButton>
      )}

      {(isValveCatalogProduct(catalogProduct) || isSpineLoadCatalogProduct(catalogProduct)) &&
        showCustomData && <ValveOrSpineLoadCustomDataListItems catalogProduct={catalogProduct} />}

      {catalogProduct.description !== null && catalogProduct.description !== '' && (
        <ListItemButton key="description">
          <ListItemText primary={catalogProduct.description} secondary={t('description')} />
        </ListItemButton>
      )}

      {isSpineMultiMeasurementCatalogProduct(catalogProduct) && (
        <>
          <ListItemButton key="outputs">
            <ListItemText primary={formatOutputs(catalogProduct)} secondary={t('outputs')} />
          </ListItemButton>

          {catalogProduct.customData?.shipAsInstallationKit != null && (
            <ListItemButton key="shipAsInstallationKit">
              <ListItemText
                primary={
                  catalogProduct.customData?.shipAsInstallationKit
                    ? t('actions:yes')
                    : t('actions:nope')
                }
                secondary={t('shipAsInstallationKit')}
              />
            </ListItemButton>
          )}
        </>
      )}
    </List>
  )
}

interface ValveOrSpineLoadCustomDataListItemsProps {
  catalogProduct: ValveCatalogProduct | SpineLoadCatalogProduct
}

function ValveOrSpineLoadCustomDataListItems({
  catalogProduct
}: ValveOrSpineLoadCustomDataListItemsProps) {
  const { t } = useTranslation('catalogProduct')

  const { spine_config, electricalCabinet } = catalogProduct.customData ?? {}

  const { heater, inRuuviTag, outRuuviTag } = isValveCatalogProduct(catalogProduct)
    ? (catalogProduct.customData ?? {})
    : {}

  const generator = isSpineLoadCatalogProduct(catalogProduct)
    ? catalogProduct.customData?.generator
    : undefined

  const electricalCabinetCatalogProduct = useLinkedCatalogProduct(electricalCabinet)

  const generatorCatalogProduct = useLinkedCatalogProduct(generator)

  const heaterCatalogProduct = useLinkedCatalogProduct(heater)

  const inRuuviTagCatalogProduct = useLinkedCatalogProduct(inRuuviTag)

  const outRuuviTagCatalogProduct = useLinkedCatalogProduct(outRuuviTag)

  /**
   * The electrical cabinet values come from the linked electrical cabinet catalog product.
   */
  const electricalCabinetCustomData =
    electricalCabinetCatalogProduct &&
    isElectricalCabinetOrSpineModuleCatalogProduct(electricalCabinetCatalogProduct)
      ? electricalCabinetCatalogProduct.customData
      : undefined

  /**
   * The generator values come from the linked generator catalog product.
   */
  const generatorCustomData =
    generatorCatalogProduct && isGeneratorCatalogProduct(generatorCatalogProduct)
      ? generatorCatalogProduct.customData
      : undefined

  /**
   * The heater values come from the linked heater catalog product.
   */
  const heaterCustomData =
    heaterCatalogProduct && isHeaterCatalogProduct(heaterCatalogProduct)
      ? heaterCatalogProduct.customData
      : undefined

  /**
   * The keys of the expanded sections.
   */
  const [expanded, setExpanded] = useState<string[]>([])

  /**
   * Toggle the section with the given key.
   */
  function toggleExpanded(key: string) {
    setExpanded((keys) =>
      keys.includes(key) ? keys.filter((value) => value !== key) : [...keys, key]
    )
  }

  return (
    <>
      {spine_config && (
        <>
          <ListSubheader>{t('powerSupply')}</ListSubheader>
          {spine_config.max_current != null && (
            <ListItemButton key="spine_config.max_current" sx={{ ml: 2 }}>
              <ListItemText
                primary={`${spine_config.max_current} A`}
                secondary={t('nominalCurrent')}
              />
            </ListItemButton>
          )}
          {spine_config.clamp_ratio != null && (
            <ListItemButton key="spine_config.clamp_ratio" sx={{ ml: 2 }}>
              <ListItemText
                primary={`${spine_config.clamp_ratio}:1`}
                secondary={t('common:clampRatio')}
              />
            </ListItemButton>
          )}
          {spine_config.input_cables != null && (
            <ListItemButton key="spine_config.input_cables" sx={{ ml: 2 }}>
              <ListItemText primary={spine_config.input_cables} secondary={t('inputCables')} />
            </ListItemButton>
          )}
          {spine_config.single_phase != null && (
            <ListItemButton key="spine_config.single_phase" sx={{ ml: 2 }}>
              <ListItemText
                primary={
                  spine_config.single_phase === 1 ? t('common:singlePhase') : t('common:threePhase')
                }
                secondary={t('common:phase')}
              />
            </ListItemButton>
          )}
        </>
      )}

      {electricalCabinet?.catalogProductId && (
        <>
          <ListSubheader>{t('electricalCabinet')}</ListSubheader>
          <LinkedCatalogProductListItem
            label={t('catalogProduct')}
            sx={{ ml: 2 }}
            link={{ ...electricalCabinet, catalogProduct: electricalCabinetCatalogProduct }}
          />
          {electricalCabinetCatalogProduct?.manufacturer && (
            <CatalogProductManufacturerListItem
              catalogProduct={electricalCabinetCatalogProduct}
              sx={{ ml: 2 }}
            />
          )}
          {electricalCabinetCustomData?.fuse != null && (
            <ListItemButton key="electricalCabinet.fuse" sx={{ ml: 2 }}>
              <ListItemText
                primary={`${electricalCabinetCustomData.fuse} A`}
                secondary={t('nominalCurrent')}
              />
            </ListItemButton>
          )}
          {electricalCabinetCustomData?.inputCables != null && (
            <ListItemButton key="electricalCabinet.inputCables" sx={{ ml: 2 }}>
              <ListItemText
                primary={electricalCabinetCustomData.inputCables}
                secondary={t('inputCables')}
              />
            </ListItemButton>
          )}
          {electricalCabinetCustomData?.isMainDistributionAssembly != null && (
            <ListItemButton key="electricalCabinet.isMainDistributionAssembly" sx={{ ml: 2 }}>
              <ListItemText
                primary={
                  electricalCabinetCustomData.isMainDistributionAssembly
                    ? t('actions:yes')
                    : t('actions:nope')
                }
                secondary={t('mainDistributionAssembly')}
              />
            </ListItemButton>
          )}
        </>
      )}

      {generator?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('generator')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('generator')}
            {expanded.includes('generator') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('generator')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              sx={{ ml: 2 }}
              link={{ ...generator, catalogProduct: generatorCatalogProduct }}
            />
            {generatorCatalogProduct?.manufacturer && (
              <CatalogProductManufacturerListItem
                catalogProduct={generatorCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
            {generatorCustomData?.apparentPower != null && (
              <ListItemButton key="generator.apparentPower" sx={{ ml: 2 }}>
                <ListItemText
                  primary={formatApperentPower(generatorCustomData.apparentPower)}
                  secondary={t('generator:apparentPower')}
                />
              </ListItemButton>
            )}
            {generatorCustomData?.nominalVoltage != null && (
              <ListItemButton key="generator.nominalVoltage" sx={{ ml: 2 }}>
                <ListItemText
                  primary={formatNominalVoltage(generatorCustomData.nominalVoltage)}
                  secondary={t('generator:nominalVoltage')}
                />
              </ListItemButton>
            )}
            {generatorCustomData?.nominalCurrent != null && (
              <ListItemButton key="generator.nominalCurrent" sx={{ ml: 2 }}>
                <ListItemText
                  primary={formatNominalCurrent(generatorCustomData.nominalCurrent)}
                  secondary={t('generator:nominalCurrent')}
                />
              </ListItemButton>
            )}
            {generatorCustomData?.powerSource != null && (
              <ListItemButton key="generator.powerSource" sx={{ ml: 2 }}>
                <ListItemText
                  primary={t(`generator:${generatorCustomData.powerSource}`)}
                  secondary={t('generator:powerSource')}
                />
              </ListItemButton>
            )}
          </Collapse>
        </>
      )}

      {heater?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('heater')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('heater')}
            {expanded.includes('heater') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('heater')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              sx={{ ml: 2 }}
              link={{ ...heater, catalogProduct: heaterCatalogProduct }}
            />
            {heaterCustomData?.type != null && (
              <ListItemButton key="heater.type" sx={{ ml: 2 }}>
                <ListItemText
                  primary={t(`heaterTypes.${heaterCustomData.type}`)}
                  secondary={t('heaterType')}
                />
              </ListItemButton>
            )}
            {heaterCustomData?.heatingSource != null && (
              <ListItemButton key="heater.heatingSource" sx={{ ml: 2 }}>
                <ListItemText
                  primary={t(`heatingSources.${heaterCustomData.heatingSource}`)}
                  secondary={t('heatingSource')}
                />
              </ListItemButton>
            )}
            {heaterCustomData?.heatingPower != null && (
              <ListItemButton key="heater.heatingPower" sx={{ ml: 2 }}>
                <ListItemText
                  primary={`${heaterCustomData.heatingPower} kW`}
                  secondary={t('heater:heatingPower')}
                />
              </ListItemButton>
            )}
          </Collapse>
        </>
      )}

      {inRuuviTag?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('inRuuviTag')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('inRuuviTag')}
            {expanded.includes('inRuuviTag') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('inRuuviTag')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              link={{ ...inRuuviTag, catalogProduct: inRuuviTagCatalogProduct }}
              sx={{ ml: 2 }}
            />
            {inRuuviTagCatalogProduct?.manufacturer && (
              <CatalogProductManufacturerListItem
                catalogProduct={inRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
            {inRuuviTagCatalogProduct?.manufacturerProductCode && (
              <CatalogProductManufacturerProductCodeListItem
                catalogProduct={inRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
          </Collapse>
        </>
      )}

      {outRuuviTag?.catalogProductId && (
        <>
          <ListSubheader
            onClick={() => toggleExpanded('outRuuviTag')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer'
            }}
          >
            {t('outRuuviTag')}
            {expanded.includes('outRuuviTag') ? <ExpandLess /> : <ExpandMore />}
          </ListSubheader>
          <Collapse in={expanded.includes('outRuuviTag')}>
            <LinkedCatalogProductListItem
              label={t('catalogProduct')}
              link={{ ...outRuuviTag, catalogProduct: outRuuviTagCatalogProduct }}
              sx={{ ml: 2 }}
            />
            {outRuuviTagCatalogProduct?.manufacturer && (
              <CatalogProductManufacturerListItem
                catalogProduct={outRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
            {outRuuviTagCatalogProduct?.manufacturerProductCode && (
              <CatalogProductManufacturerProductCodeListItem
                catalogProduct={outRuuviTagCatalogProduct}
                sx={{ ml: 2 }}
              />
            )}
          </Collapse>
        </>
      )}
    </>
  )
}

interface LinkedCatalogProductListItemProps extends Omit<ListItemButtonProps, 'children'> {
  label: string
  link: CatalogProductLink
}

/**
 * Shows the name of a linked catalog product.
 */
function LinkedCatalogProductListItem({
  label,
  link,
  ...props
}: LinkedCatalogProductListItemProps) {
  const linkedCatalogProduct = useLinkedCatalogProduct(link)

  return (
    <ListItemButton {...props}>
      <ListItemText primary={linkedCatalogProduct?.name ?? '-'} secondary={label} />
    </ListItemButton>
  )
}

export interface CatalogProductDetailsListItemProps extends Omit<ListItemButtonProps, 'children'> {
  catalogProduct: CatalogProduct
}

export function CatalogProductNameListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.name ?? t('noName')}
        primaryTypographyProps={{
          color: catalogProduct.name ? 'inherit' : 'text.disabled'
        }}
        secondary={t('name')}
      />
    </ListItemButton>
  )
}

export function CatalogProductManufacturerListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.manufacturer ?? t('noManufacturer')}
        primaryTypographyProps={{
          color: catalogProduct.manufacturer ? 'inherit' : 'text.disabled'
        }}
        secondary={t('manufacturer')}
      />
    </ListItemButton>
  )
}

export function CatalogProductManufacturerProductCodeListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.manufacturerProductCode ?? t('noManufacturerProductCode')}
        primaryTypographyProps={{
          color: catalogProduct.manufacturerProductCode ? 'inherit' : 'text.disabled'
        }}
        secondary={t('manufacturerProductCode')}
      />
    </ListItemButton>
  )
}

interface CatalogProductPhotoProps {
  catalogProduct: CatalogProduct
}

/**
 * Shows the photo of a catalog product.
 */
export function CatalogProductPhoto({ catalogProduct }: CatalogProductPhotoProps) {
  const [catalogProductPhotoUrl, setCatalogProductPhotoUrl] = useState<string | null>(null)

  const loadCatalogProductPhoto = useCallback(async (photoId: string, signal: AbortSignal) => {
    try {
      const result = await uploadService.getUpload(photoId, { signal })
      setCatalogProductPhotoUrl(result.url)
    } catch {
      if (!signal.aborted) {
        setCatalogProductPhotoUrl(null)
      }
    }
  }, [])

  /**
   * The photo relation is not always loaded, but the photo ID is.
   */
  const photoId = catalogProduct.photo?.id ?? catalogProduct.photoId

  useEffect(() => {
    const abortController = new AbortController()

    if (photoId === undefined || photoId === null) {
      setCatalogProductPhotoUrl(null)
      return
    }

    loadCatalogProductPhoto(photoId, abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [photoId, loadCatalogProductPhoto])

  if (!catalogProductPhotoUrl) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        bgcolor: 'background.default',
        borderRadius: 1,
        px: 2,
        py: 8
      }}
    >
      <ImageWithPreview
        src={catalogProductPhotoUrl}
        alt={catalogProduct.name}
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: '280px',
          objectFit: 'contain'
        }}
      />
    </Box>
  )
}
